import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SmartImage from "@/components/base/SmartImage";
import { products } from "@/mocks/products";

// Dedicated Technology Hero placeholder — replace with final asset when supplied.
const TECH_HERO = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_technology-hero.webp";

// Shared split tokens — identical height and padding across all technology sections.
const SPLIT_H = "h-auto min-h-[380px] md:h-[540px]";
const SPLIT_IMG_H = "h-[300px] md:h-[540px]";
const SPLIT_PAD = "px-5 md:px-10 lg:px-14 py-10 md:py-0";

const chapters = [
  {
    code: "01",
    title: "Weather Protection",
    intro: "Water beads and rolls away from the shell surface.",
    body: "Our 3-layer shells are built around a fully seam-taped construction and a durable water-repellent finish that keeps performing across seasons. Every finish is renewable — we design for the shell you'll still be wearing in year five.",
    img: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/f611a582-4342-4eba-8ce2-f39ec1127145_compressed_home-tech-water-beading-red-web.webp",
    tag: "Weather Protection",
  },
  {
    code: "02",
    title: "Engineered Construction",
    intro: "Sealed zipper and precise panel construction.",
    body: "Panel geometry, seam placement and zipper engineering are decided in the field, not in the CAD file. Ripstop weaves, bar-tack reinforcements and repair-friendly hardware mean the gear you buy stays in service for years.",
    img: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/86ac82bb-5438-43d2-8a04-298f235438cb_compressed_home-tech-zip-seam-charcoal-web.webp",
    tag: "Engineered Construction",
  },
  {
    code: "03",
    title: "Breathable Performance",
    intro: "Layered textile structure designed for airflow.",
    body: "Airflow is engineered layer-by-layer. Grid fleece backers, mesh linings and mapped ventilation panels are combined to move moisture out and hold warmth where you need it.",
    img: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/771e4ac9-a59d-4db9-8eca-4a06edbe7dc6_compressed_home-tech-breathable-fabric-moss-web.webp",
    tag: "Breathable Performance",
  },
];

export default function TechnologyPage() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="overlay" />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative w-full h-[440px] md:h-[640px] overflow-hidden bg-background-100">
          <SmartImage src={TECH_HERO} alt="Technology" tone="dark" eager imgClassName="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/55"></div>
          <div className="relative z-10 h-full w-full px-5 md:px-10 pb-12 md:pb-20 flex flex-col justify-end">
            <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-100/85 mb-4">Material Story</p>
            <h1 className="text-[34px] md:text-[54px] leading-[1.08] md:leading-[1.02] tracking-[-0.02em] text-foreground-50 font-light max-w-3xl">
              Performance engineered<br className="hidden md:block" /> into every fiber.
            </h1>
          </div>
        </section>

        {chapters.map((c, i) => {
          const imageRight = i % 2 === 1;
          const productSlug = products.find((p) => p.tech.includes(c.tag))?.categorySlug ?? "outerwear";
          const image = (
            <div key="img" className={`relative w-full ${SPLIT_IMG_H} overflow-hidden bg-background-200`}>
              <SmartImage src={c.img} alt={c.title} tone="dark" imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
            </div>
          );
          const text = (
            <div key="txt" className={`${SPLIT_PAD} flex flex-col justify-center ${SPLIT_H}`}>
              <div className="flex items-center gap-3 mb-4 max-w-md">
                <span className="text-[10px] tracking-[0.24em] uppercase text-primary-400">{c.code}</span>
                <span className="h-px flex-1 bg-background-200/60"></span>
              </div>
              <h2 className="text-[28px] md:text-[30px] leading-[1.12] md:leading-[1.1] tracking-[-0.01em] text-foreground-50 font-light mb-3 max-w-md">{c.title}</h2>
              <p className="text-[16px] md:text-[15px] text-foreground-200 mb-4 max-w-md">{c.intro}</p>
              <p className="text-[14px] md:text-[13px] text-foreground-300 leading-relaxed max-w-md mb-6">{c.body}</p>
              <Link
                to={`/shop?cat=${productSlug}`}
                className="inline-flex items-center gap-2 text-[13px] text-foreground-50 hover:text-primary-400 border-b border-foreground-300/50 hover:border-primary-400 pb-1 cursor-pointer whitespace-nowrap w-fit"
              >
                Explore products using this technology <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          );
          return (
            <section key={c.code} className={`${i % 2 === 0 ? "bg-background-50" : "bg-background-100"} border-t border-white/5`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
                {imageRight ? (
                  <>
                    <div className="order-2 lg:order-1">{text}</div>
                    <div className="order-1 lg:order-2">{image}</div>
                  </>
                ) : (
                  <>
                    {image}
                    {text}
                  </>
                )}
              </div>
            </section>
          );
        })}
      </main>
      <SiteFooter />
    </div>
  );
}